import {
  APP_INSIGHTS_EVENT_TYPE,
  CustomEventProperties,
  trackCustomEvent,
} from 'common/utils/telemetry';
import {
  SurveyResponse,
  SurveyAnswer,
  QuestionWithExtras,
  Category,
  OrganicInsight,
  SetRemoveInsightPayload,
  OrganicResponse,
  OrganicOption,
} from '../../../../../common/models/insights-capture.model';

export const removeInsight = ({
  removeInsight,
  insights,
}: {
  removeInsight: SetRemoveInsightPayload;
  insights: SurveyAnswer[];
}) => {
  return insights
    .map((stateInsight: SurveyAnswer) => {
      if (stateInsight.surveyId === removeInsight.insight.surveyId) {
        return {
          ...stateInsight,
          questions: stateInsight.questions.filter(
            (qu) => qu.surveyQuestionId !== removeInsight.questionId
          ),
        };
      }
      return { ...stateInsight };
    })
    .filter((stateInsight) => stateInsight.questions.length > 0);
};

export const updateOrganicInsight = ({
  insight,
  organicInsights,
}: {
  insight: OrganicInsight;
  organicInsights: OrganicInsight[];
}) => {
  if (organicInsights.length) {
    return organicInsights?.map((stateInsight: OrganicInsight) => ({
      surveyQuestionResponseId:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.surveyQuestionResponseId
          : stateInsight.surveyQuestionResponseId,
      surveyQuestionResponseOptionId:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.surveyQuestionResponseOptionId
          : stateInsight.surveyQuestionResponseOptionId,
      freeText:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.freeText
          : stateInsight.freeText,
      imageSource:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.imageSource
          : stateInsight.imageSource,
      fileName:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.fileName
          : stateInsight.fileName,
      base64String:
        stateInsight.surveyQuestionResponseOptionId === insight.surveyQuestionResponseOptionId
          ? insight.base64String
          : stateInsight.base64String,
    }));
  }
  return [insight];
};

export const addNewInsight = ({
  insight,
  insights,
  additionalTrackingInfo,
}: {
  insight: SurveyAnswer;
  insights: SurveyAnswer[];
  additionalTrackingInfo: CustomEventProperties;
}) => {
  if (!insights?.length) {
    trackCustomEvent({
      type: APP_INSIGHTS_EVENT_TYPE.TRACE,
      name: 'addNewInsight - no insights',
      properties: {
        ...additionalTrackingInfo,
        insight,
        insights,
      },
    });
    return [insight];
  }

  let insightAdded = false;

  const result = insights.map((stateInsight) => {
    if (stateInsight.surveyId === insight.surveyId) {
      insightAdded = true;
      return {
        ...stateInsight,
        questions: [
          ...stateInsight.questions.filter((qu) => {
            trackCustomEvent({
              type: APP_INSIGHTS_EVENT_TYPE.TRACE,
              name: 'addNewInsight - filter question',
              properties: {
                ...additionalTrackingInfo,
                qu,
                insightQuestion: insight.questions[0],
              },
            });
            return qu.surveyQuestionId !== insight.questions[0].surveyQuestionId;
          }),
          ...insight.questions,
        ],
      };
    }
    return stateInsight;
  });

  trackCustomEvent({
    type: APP_INSIGHTS_EVENT_TYPE.TRACE,
    name: 'addNewInsight - result',
    properties: {
      ...additionalTrackingInfo,
      result,
      insights,
    },
  });

  return insightAdded ? result : [...result, insight];
};

export const getActivityOrganicInsightsFromResponse = (response: OrganicResponse[]) => {
  const options = response?.[0].surveyQuestionResponseOptions;
  const organicData = options?.map((option: OrganicOption) => {
    const base64File =
      typeof option.surveyQuestionResponseFile === 'string'
        ? option.surveyQuestionResponseFile
        : '';
    return {
      freeText: option.surveyQuestionResponseOptionText,
      base64String: option.surveyQuestionResponseFile,
      imageSource: option.fileMimeType ? `data:${option.fileMimeType};base64,${base64File}` : '',
      fileName: option.surveyQuestionResponseFileName,
      fileMimeType: option.fileMimeType,
      surveyQuestionResponseOptionId: option.surveyQuestionResponseOptionId,
      surveyQuestionResponseId: option.surveyQuestionResponseId,
    };
  });

  return organicData.sort(function (a, b) {
    return a.surveyQuestionResponseOptionId - b.surveyQuestionResponseOptionId;
  });
};

export const getActivityInsightsFromResponse = (
  responses: SurveyResponse[],
  categories: Category[]
) => {
  const surveyInsights = responses?.reduce(
    (acc: Array<SurveyAnswer>, response: any): SurveyAnswer[] => {
      if (response.isOrganicInsightsSurvey) {
        return [...acc];
      }
      const category = categories?.find((category) => category.surveyId === response.surveyId);
      const question = category?.questions.find(
        (question) => question.surveyQuestionId === response.surveyQuestionId
      );

      const existingSurvey = acc?.find((val) => val.surveyId === response.surveyId);

      const base64File =
        typeof response.surveyQuestionResponseFile === 'string'
          ? response.surveyQuestionResponseFile
          : '';
      const questions: QuestionWithExtras[] = [
        {
          ...question,
          surveyQuestionId: question?.surveyQuestionId ?? 0,
          surveyQuestionName: question?.surveyQuestionName ?? '',
          surveyQuestionText: question?.surveyQuestionText ?? '',
          surveyQuestionType: question?.surveyQuestionType ?? response.surveyQuestionType,
          surveyQuestionResponseId: response.surveyQuestionResponseId,
          surveyQuestionOptions:
            question?.surveyQuestionOptions.filter((option) =>
              response.surveyQuestionResponseOptionsIds.includes(option.surveyQuestionOptionId)
            ) || [],
          freeText: response.subsequentResponseText,
          base64String: response.surveyQuestionResponseFile,
          imageSource: response.fileMimeType
            ? `data:${response.fileMimeType};base64,${base64File}`
            : '',
          fileName: response.surveyQuestionResponseFileName,
          fileMimeType: response.fileMimeType,
          textInputValue: response.surveyQuestionResponseText,
        },
      ];

      //different question in same survey
      if (existingSurvey) {
        return [
          ...acc.map((ac) => ({
            ...ac,
            questions:
              ac.surveyId === response.surveyId //if the survey is the same then add question to that survey otherwise separate it
                ? [...ac.questions, ...questions]
                : [...ac.questions],
          })),
        ];
      }

      const insight: SurveyAnswer = {
        surveyId: response.surveyId || '0',
        surveyName: category?.surveyName ?? '',
        isOrganicInsight: category?.isOrganicInsightsSurvey,
        questions,
        isExpired: category?.isExpired || false,
      };

      return [...acc, { ...insight }];
    },
    []
  );
  return surveyInsights || [];
};
